import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { TinyChip } from '../../TinyChip';

/**
 * The label the CMDB provider puts on a service provider - a team that
 * provides or contributes services to the platform - see
 * `LABEL_SERVICE_PROVIDER` in the catalog backend module.
 */
export const LABEL_SERVICE_PROVIDER = 'schema.dp.netrtl.com/service-provider';

/**
 * Whether the CMDB provider labels the team a service provider.
 *
 * @public
 */
export function isServiceProvider(entity: Entity): boolean {
  return entity.metadata?.labels?.[LABEL_SERVICE_PROVIDER] === 'true';
}

/**
 * What marks a service provider wherever a team is named: a tiny chip in the
 * green of the theme with a solid outline - a role the team has, unlike the
 * dashed virtual chip, which says what the team is not.
 *
 * Shown to everyone, unlike the rest of the platform catalog behind the
 * `platform-catalog` flag: which teams provide services is no work in
 * progress.
 *
 * @public
 */
export const ServiceProviderChip = () => (
  <TinyChip
    label="provider"
    tooltip="Provides or contributes services, e.g. to the Developer Platform."
    color="success"
  />
);

/**
 * Narrows a listing to the service providers, or leaves it alone.
 *
 * It stands in the url either way, as `filters[serviceProvidersOnly]=true` or
 * `false`. That is how a link - the Service Providers entry of the Catalog
 * menu - opens the catalog on these teams, and how the menu tells that entry
 * from Groups: it highlights every entry whose link the url contains, and with
 * the switch left out of the url the link of Groups would be part of that of
 * Service Providers.
 *
 * @public
 */
export class EntityServiceProvidersOnlyFilter implements EntityFilter {
  constructor(readonly onlyServiceProviders: boolean = true) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return this.onlyServiceProviders
      ? { [`metadata.labels.${LABEL_SERVICE_PROVIDER}`]: 'true' }
      : {};
  }

  filterEntity(entity: Entity): boolean {
    return (
      !this.onlyServiceProviders ||
      entity.metadata.labels?.[LABEL_SERVICE_PROVIDER] === 'true'
    );
  }

  toQueryValue(): string {
    return String(this.onlyServiceProviders);
  }
}

/** The filters of a listing that can be narrowed to the service providers. */
export type ServiceProvidersOnlyEntityFilters = DefaultEntityFilters & {
  serviceProvidersOnly?: EntityServiceProvidersOnlyFilter;
};

/**
 * Props for {@link ServiceProvidersOnlyPicker}.
 *
 * @public
 */
export type ServiceProvidersOnlyPickerProps = {
  /**
   * Narrow for good and show nothing, for a page that is about the service
   * providers and offers no choice.
   */
  hidden?: boolean;
};

/**
 * Narrows a listing of groups to the service providers, with a switch to do
 * so.
 *
 * Off by default, on when the url says so. Only groups carry the label, so a
 * listing of any other kind gets no switch.
 *
 * @public
 */
export const ServiceProvidersOnlyPicker = (
  props: Readonly<ServiceProvidersOnlyPickerProps>,
) => {
  const { hidden = false } = props;
  const { filters, updateFilters, queryParameters } =
    useEntityList<ServiceProvidersOnlyEntityFilters>();

  const kind = filters.kind?.value?.toLocaleLowerCase('en-US');
  const listsGroups = hidden || kind === 'group';

  const queried = [queryParameters.serviceProvidersOnly].flat()[0];
  const [onlyServiceProviders, setOnlyServiceProviders] = useState(
    hidden || queried === 'true',
  );

  // The catalog page stays where it is when a menu entry leads from one of
  // its listings to another, so the switch follows the url rather than only
  // reading it once.
  useEffect(() => {
    if (!hidden && queried !== undefined) {
      setOnlyServiceProviders(queried === 'true');
    }
  }, [hidden, queried]);

  useEffect(() => {
    updateFilters({
      serviceProvidersOnly: listsGroups
        ? new EntityServiceProvidersOnlyFilter(onlyServiceProviders)
        : undefined,
    });
  }, [listsGroups, onlyServiceProviders, updateFilters]);

  if (hidden || !listsGroups) {
    return null;
  }

  return (
    <Box pb={1} pt={1}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={onlyServiceProviders}
            onChange={event => setOnlyServiceProviders(event.target.checked)}
          />
        }
        label="Service providers only"
      />
    </Box>
  );
};
